import { PageLink } from '@ipfabric/ui-components/Link/Link';
import { type Filter, createTableFilterLinkProps } from '@ipfabric/utils';
import { is } from '@ipfabric/utils/fp/comparison';
import { option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { toNullable } from 'fp-ts/lib/Option';
import type { ReactNode } from 'react';

const getRoute = (protocol: string) => {
  switch (protocol) {
    case 'cdp':
    case 'lldp':
    case 'cdp lldp':
      return '/technology/cdp-lldp/unmanaged-neighbors';
    case 'cef':
      return '/technology/routing/routes';
    case 'bgp':
      return '/technology/routing/bgp/neighbors';
    case 'isis':
      return '/technology/routing/is-is/neighbors';
    case 'ospf':
      return '/technology/routing/ospf/neighbors';
    case 'eigrp':
      return '/technology/routing/eigrp/neighbors';
    case 'rip':
      return '/technology/routing/rip/neighbors';
    case 'ldp':
      return '/technology/mpls/ldp/neighbors';
    default:
      return '';
  }
};

const getFilterColumn = (protocol: string) => {
  switch (protocol) {
    case 'cdp':
    case 'lldp':
    case 'cdp lldp':
      return 'remoteIp';
    case 'cef':
      return 'nexthop';
    case 'bgp':
      return 'neiAddress';
    case 'isis':
      return 'neiAddress';
    case 'ospf':
      return 'neiAddress';
    case 'eigrp':
      return 'neiAddress';
    case 'rip':
      return 'neiAddress';
    case 'ldp':
      return 'neiAddress';
    default:
      return '';
  }
};

const getFilterValue = (value: string, protocol: string): Filter =>
  pipe(
    protocol,
    option.fromPredicate(is('cef')),
    option.fold(
      (): Filter => ['eq', value],
      (): Filter => ['any', 'ip', 'eq', value],
    ),
  );

export const wrapNeighborIpWithLink = (
  value: string,
  protocol: string,
  rowData: unknown,
  contrast: boolean,
): ReactNode => {
  const route = getRoute(protocol);
  const filterColumn = getFilterColumn(protocol);

  return pipe(
    value,
    option.fromPredicate(() => !!value && !!route && !!filterColumn),
    option.map((node) => (
      <PageLink
        accent={contrast ? 'contrast' : 'positive'}
        key={node}
        {...createTableFilterLinkProps(
          route,
          () => ({
            [filterColumn]: getFilterValue(node, protocol),
          }),
          rowData,
        )}
      >
        {node}
      </PageLink>
    )),
    toNullable,
  );
};
